import React, { useCallback, useState } from "react";

import {
  createQColumnHelper,
  DataProvider,
  QButton,
  QButtonGroup,
  QCard,
  QCollapse,
  QDataTable,
  QHeading,
  QIconButton,
  QStack,
  QTag,
  QText,
} from "@qualio/ui-components";
import { useTranslation } from "react-i18next";
import { TestResult } from "@design-controls/types";
import { ResultValueToLabel, TestResultVariantColour } from "../../constants";
import { useNavigate } from "react-router";
import { AttachmentTable } from "./AttachmentTable";

const columnHelper = createQColumnHelper<any>();

type Props = {
  result?: TestResult;
};

export const TestResultTable: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();

  const addTestResult = useCallback(() => navigate("add-result"), [navigate]);

  const columns = [
    columnHelper.date("date", {
      header: t("testCases.testResult.table.header.date"),
      minWidth: "100px",
      width: "10%",
    }),
    columnHelper.text("executedBy", {
      header: t("testCases.testResult.table.header.tester"),
      minWidth: "200px",
      width: "20%",
    }),
    columnHelper.text("notes", {
      header: t("testCases.testResult.table.header.comments"),
      minWidth: "200px",
      width: "55%",
    }),
    {
      header: t("testCases.testResult.table.header.lastResults"),
      meta: {
        width: "10%",
        minWidth: "150px",
      },
      cell: ({ row: { original: row } }: { row: { original: TestResult } }) => {
        return (
          <QTag variantColor={TestResultVariantColour[row.result]}>
            {ResultValueToLabel[row.result]}
          </QTag>
        );
      },
    },
  ];

  return (
    <QCard>
      <QStack
        direction="row"
        justifyContent="space-between"
        padding="12px 16px"
      >
        <QHeading fontSize="md" fontWeight={600}>
          {t("testCases.testResult.table.heading")}
        </QHeading>
        <QButtonGroup>
          <QButton variant="link" onClick={addTestResult}>
            {t("testCases.testResult.actions.addTestResult")}
          </QButton>
          <QIconButton
            onClick={() => setExpanded((s) => !s)}
            iconName={expanded ? "ChevronUp" : "ChevronDown"}
            aria-label={
              expanded
                ? t("reports.collapseExpand.collapse")
                : t("reports.collapseExpand.expand")
            }
          />
        </QButtonGroup>
      </QStack>
      <QCollapse isOpen={expanded}>
        {result && (
          <DataProvider.Fixed data={[result]}>
            <QDataTable columns={columns} hideItemCount data-cy="test-result" />
          </DataProvider.Fixed>
        )}
        {!!result?.attachments?.length && (
          <QStack margin={2}>
            <QStack gap="8px">
              <QHeading fontSize="md" fontWeight={600}>
                {t("common.attachments")}
              </QHeading>
              <QText fontSize="sm">
                {t("testCases.testResult.attachmentsTableDescription")}
              </QText>
            </QStack>
            <AttachmentTable attachments={result?.attachments} withoutBorder />
          </QStack>
        )}
      </QCollapse>
    </QCard>
  );
};
